import React, { Dispatch, SetStateAction, useState } from 'react';
import { IconButton } from '../elements';
import { ReactComponent as StarInactiveIcon } from '../icons/round-star_border_inactive.svg';
import { ReactComponent as StarActiveIcon } from '../icons/round-star_border_active.svg';
import { LiveBadge } from '../Results/LiveBadge';
import { StoreType } from '../../store/spirs/types';
import { MoreLessToggleIcon } from '../custom/MoreLessToggleIcon';
import { MarketVerbose } from './MarketVerbose';
import {
    CompetitorElement,
    EventClockElement,
    FixtureElement,
    FixtureStatus,
    GroupedMarketElements,
    MatchStatusElement,
    PeriodElement,
    PeriodScoreElement,
    TeamStatisticsElement
} from '../../models/api/codegen/code';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from '../../utils/FormatDate';
import { useTranslation } from 'react-i18next';
import { ReactComponent as BackChevronIcon } from '../icons/chevron_back.svg';
import {ReactComponent as CopyIcon}  from "../icons/copy-paste-select-add-plus.svg";
import styled from 'styled-components';
import { useFavorites } from '../../utils/FavoritesUtils';
import { Visible } from '../../utils/Visible';
import clsx from 'clsx';
import { hex2rgba } from '../../utils/ColorUtils';
import { BackButton } from '../Common/BackButton';
import { PeriodName } from '../score/PeriodName';
import { WidgetShowButton } from '../WidgetsPane/WidgetShowButton';
import { WidgetsPane } from '../WidgetsPane/WidgetsPane';
import { useNavigate } from 'react-router-dom';
import {
    ChessImgPath1,
    ChessImgPath10,
    ChessImgPath11,
    ChessImgPath12,
    ChessImgPath13,
    ChessImgPath14,
    ChessImgPath15,
    ChessImgPath16,
    ChessImgPath17,
    ChessImgPath18,
    ChessImgPath19,
    ChessImgPath2,
    ChessImgPath20,
    ChessImgPath21,
    ChessImgPath22,
    ChessImgPath23,
    ChessImgPath24,
    ChessImgPath3,
    ChessImgPath4,
    ChessImgPath5,
    ChessImgPath6,
    ChessImgPath7,
    ChessImgPath8,
    ChessImgPath9,
    FootballPitchImgPath,
    LinePitchImgPath,
    LivePitchImgPath
} from '../../utils/ChessImgPaths';
import { useTestOverlay } from '../coupons/TestOverlayContext';
import { TruncatedText } from '../custom/TruncatedText';
import { ReactComponent as MatchInfoIcon } from '../CommonSportDisplay/icons/match_info_icon.svg';
import { SHOW_WIDGET } from '../../store/spirs/actions';
import LogoFavorite from '../CommonSportDisplay/icons/specialFavoriteLogo';
import { IScores, scoreTableParser } from './scroresParser';
import { t } from 'i18next';
import {IframeToast} from "./Toast";
import { makeSelectRouteTreeType } from '../VerticalMenu/selectors';
import {Desktop} from "../adaptive/Desktop";
import {Mobile} from "../adaptive/Mobile";

const getImage = (fixture: FixtureElement): string => {
    if (!fixture.cl) {
        if (fixture.s === 'LIVE') {
            return LivePitchImgPath;
        } else {
            return LinePitchImgPath;
        }
    }
    switch (fixture.fi) {
        case 1:
        case 25:
        case 49:
            return ChessImgPath1;
        case 2:
        case 26:
        case 50:
            return ChessImgPath2;
        case 3:
        case 27:
        case 51:
            return ChessImgPath3;
        case 4:
        case 28:
        case 52:
            return ChessImgPath4;
        case 5:
        case 29:
        case 53:
            return ChessImgPath5;
        case 6:
        case 30:
        case 54:
            return ChessImgPath6;
        case 7:
        case 31:
        case 55:
            return ChessImgPath7;
        case 8:
        case 32:
        case 56:
            return ChessImgPath8;
        case 9:
        case 33:
        case 57:
            return ChessImgPath9;
        case 10:
        case 34:
        case 58:
            return ChessImgPath10;
        case 11:
        case 35:
        case 59:
            return ChessImgPath11;
        case 12:
        case 36:
        case 60:
            return ChessImgPath12;
        case 13:
        case 37:
        case 61:
            return ChessImgPath13;
        case 14:
        case 38:
        case 62:
            return ChessImgPath14;
        case 15:
        case 39:
        case 63:
            return ChessImgPath15;
        case 16:
        case 40:
        case 64:
            return ChessImgPath16;
        case 17:
        case 41:
        case 65:
            return ChessImgPath17;
        case 18:
        case 42:
        case 66:
            return ChessImgPath18;
        case 19:
        case 43:
        case 67:
            return ChessImgPath19;
        case 20:
        case 44:
        case 68:
            return ChessImgPath20;
        case 21:
        case 45:
        case 69:
            return ChessImgPath21;
        case 22:
        case 46:
        case 70:
            return ChessImgPath22;
        case 23:
        case 47:
            return ChessImgPath23;
        case 24:
        case 48:
            return ChessImgPath24;
        default:
            return FootballPitchImgPath;
    }
};

export type PageType = "LIVE" | "PREMATCH" | "RATING" | "FAVORITES";

export const EventFixture = ({ hide = false, page }: EventFixtureProps) => {
    const push = useNavigate();
    const fixture = useSelector(
        (state: StoreType) => state.spirs.displayedFixture
    );
    const selectRouteTreeType = React.useMemo(makeSelectRouteTreeType, []);
    const route = useSelector((state: StoreType) => selectRouteTreeType(state));
    const initPeriod = () => {
        if (fixture && fixture.p && fixture.p.length > 0) {
            return fixture.p[0].g;
        }
        return null;
    };
    const [period, setPeriod] = React.useState<PeriodElement['g'] | null>(
        initPeriod()
    );
    const debug = useSelector((state: StoreType) => state.spirs.debugMode);
    const { isFavorite, onChange } = useFavorites(
        fixture.fi,
        useDispatch(),
        fixture.f
    );
    const { showOverlay, isConnecting, countdown } = useTestOverlay();
    const hasOverlay = (showOverlay || isConnecting) && !!countdown;

    function getRoute() {
        switch (page) {
            case 'LIVE':
            case 'PREMATCH':
            case 'RATING':
                return route;
            case 'FAVORITES':
                let path;
                if (fixture.s === 'LIVE') {
                    path = '/live'
                } else {
                    path = "/prematch"
                }
                return "/sport" + path + "/";
        }
    }

    const handleBackToSport = () => {

        !hide && push(getRoute() + fixture.sid);
    };

    const handleBackToTournament = () => {
        if (!hide) {
            push({
                pathname: `${getRoute()}${fixture.sid}/${fixture.cid}/${fixture.tid}/`,
                hash: `fixture-${fixture.fi}`
            }, {
                state: {
                    highlightedFixture: fixture.fi,
                    shouldScrollToHash: true
                }
            });
        }
    };

    React.useEffect(() => {
        !period && setPeriod(initPeriod);
    }, [fixture]);
    if (!fixture) return <div>LOADING</div>;

    if (!fixture.fi) return null;
    const FavoriteIcon = isFavorite ? StarActiveIcon : StarInactiveIcon;
    const Img = getImage(fixture);

    return (
        <div className="w-full">
            <div className="w-full">
                <div className="tablet:sticky tablet:t-0 tablet:z-[2] tablet:bg-[#272d33]">
                    <div className="max-tablet:hidden">
                        <DesktopHeader
                            fixture={fixture}
                            handleBackToSport={handleBackToSport}
                            handleBackToTournament={handleBackToTournament}
                        />
                    </div>

                    <div className="relative tablet:mb-[8px]">
                        <div className="max-tablet:hidden">
                            <picture>
                                <img
                                    className="absolute w-full h-full rounded-[4px]"
                                    src={Img}
                                    alt="img"
                                />
                            </picture>
                        </div>

                        <div
                            className={clsx(
                                'p-[8px] relative t-0 w-full tablet:rounded-[4px] max-tablet:bg-[#171A1E] max-tablet:border-b',
                                fixture.cl
                                    ? 'h-[275px] notebook:h-[400px] desktop:h-[500px]'
                                    : 'h-auto'
                            )}
                            style={{
                                ...(window.innerWidth < 822 && fixture.sc
                                    ? { borderColor: fixture.sc }
                                    : {})
                            }}
                        >
                            <div className="tablet:hidden">
                                <MobileHeader
                                    fixture={fixture}
                                    handleBackToTournament={
                                        handleBackToTournament
                                    }
                                />
                            </div>
                            <div className="grid grid-cols-[repeat(3,1fr)] items-center justify-between px-4 max-tablet:pt-4 tablet:pt-1">
                                <div
                                    className='tablet:invisible text-[18px]'
                                    style={{ color: fixture.sc }}
                                >
                                    {fixture.s === 'NOT_STARTED' ? null : fixture.fi}
                                </div>
                                <IconButton
                                    size="small"
                                    aria-label="add to favorite"
                                    onClick={onChange}
                                    className="flex justify-center"
                                >
                                    <LogoFavorite
                                        checked={isFavorite}
                                        size="24"
                                    />
                                </IconButton>
                                <div className='flex justify-end gap-2'>
                                    <Visible value={fixture.s === 'LIVE'}>
                                        <LiveBadge />
                                    </Visible>
                                    <Desktop>
                                        {fixture.fw && <WidgetShowButton mobile={false} />}
                                    </Desktop>
                                </div>
                            </div>
                            {fixture.cl ? (
                                <>
                                    <div className="max-tablet:hidden">
                                        <div />
                                    </div>
                                    <div className="flex justify-center tablet:hidden">
                                        {fixture.cn}
                                    </div>
                                </>
                            ) : (
                                <Info
                                    competitors={fixture.c}
                                    status={fixture.s}
                                    startTime={fixture.st}
                                    eventClock={
                                        fixture.ms
                                            ? fixture.ms.eventClock
                                            : undefined
                                    }
                                    periodScores={
                                        fixture.ms
                                            ? fixture.ms.periodScores
                                            : undefined
                                    }
                                    homeScore={
                                        fixture.ms
                                            ? fixture.ms.homeScore
                                            : undefined
                                    }
                                    awayScore={
                                        fixture.ms
                                            ? fixture.ms.awayScore
                                            : undefined
                                    }
                                    matchStatus={
                                        fixture.ms
                                            ? fixture.ms.matchStatus
                                            : undefined
                                    }
                                    competitorName={fixture.cn}
                                    sportId={fixture.sid}
                                    fixtureWidgetId={fixture.fw}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="max-tablet:hidden">
                    {fixture.p
                        .map(period => ({
                            name: period.n,
                            markets: fixture.gm.filter(gr =>
                                gr.m.every(market => market.pg === period.g)
                            )
                        }))
                        .map((period, i) => (
                            <GroupDesktopMarkets
                                key={i}
                                debug={debug}
                                period={period.name}
                                groupedMarkets={period.markets}
                                fixture={fixture}
                                open={fixture.p.length === 1}
                                hasOverlay={hasOverlay}
                            />
                        ))}
                </div>
                <div className="tablet:hidden">
                    <MobilePeriods
                        periods={fixture.p}
                        callback={setPeriod}
                        current={period}
                    />
                    <GroupMobileMarkets
                        period={period as string}
                        fixture={fixture}
                        debug={debug}
                        groupedMarkets={[]}
                        currentGroup={period}
                        open={fixture.p.length === 1}
                        hasOverlay={hasOverlay}
                    />
                </div>
            </div>
        </div>
    );
};

interface HeaderProps {
    handleBackToTournament: () => void;
    fixture: FixtureElement;
}

interface DesktopHeaderProps extends HeaderProps {
    handleBackToSport: () => void;
    hide?: boolean;
}

interface EventFixtureProps {
    hide?: boolean;
    page: PageType;
}

const copyLink = async () => {
      const link = window.location.origin + window.location.pathname + window.location.hash;
    try {
        await navigator.clipboard.writeText(link);
        IframeToast.info({content: t("izroats.linkIsCopied")})
    } catch (err) {
        IframeToast.error({content: t("izroats.errorlinkIsCopied")})
    }
};

const DesktopHeader: React.FC<DesktopHeaderProps> = ({handleBackToSport, handleBackToTournament, hide, fixture }) => {
    const {t} = useTranslation();
    const {web} = useSelector((state: StoreType) => state.spirs.playerInfo);
    return (
        <div className="flex items-center justify-between text-[12px] mb-[10px] gap-2">
        <div className="flex basis-0 flex-grow-0">
            <div className=" w-[max-content] flex text-[16px] px-[9px] rounded-[30px] justify-center items-center">
                    {fixture.s !== 'LIVE' ? (
                        <BackButton
                            onClick={handleBackToSport}
                            color={hex2rgba(fixture.sc, 0.25)}
                        >
                            <Visible value={!hide}>
                                <BackChevronIcon />
                            </Visible>
                            <img
                                className="w-[28px] h-[20px] mr-[10px]"
                                src={fixture.ciu}
                                alt={'flag'}
                            />{' '}
                            {fixture.ctn}. {fixture.tn}
                        </BackButton>
                    ) : (
                        <>
                            <img
                                className="w-[28px] h-[20px] mr-[10px]"
                                src={fixture.ciu}
                                alt={'flag'}
                            />{' '}
                            {fixture.ctn}. {fixture.tn}
                        </>
                    )}
                </div>
                <Visible value={fixture.s !== 'LIVE' && !hide}>
                    <BackButton
                        onClick={handleBackToTournament}
                        color={hex2rgba(fixture.sc, 0.25)}
                    ><BackChevronIcon/>
                    <div className="w-[max-content] flex text-[16px] py-[1px] px-[9px] rounded-[30px] justify-center items-center">
                        {fixture.cn}
                    </div>
                </BackButton>
            </Visible>

        </div>

            {web && <div className='desktop:w-[200px] notebook:w-[158px] netbook:w-[158px] flex items-center justify-center  h-[40px] bg-[#3FD96E] w-[max-content] text-[14px] py-[1px] px-[9px] rounded-[30px] ' style={{backgroundColor: hex2rgba(fixture.sc, 0.25)}}>
                <button
                    onClick={() => copyLink()}
                    className='w-full flex items-center justify-center font-[Roboto]'
                >
                    {t("copy-link")}
                </button>
            </div>}

        {/* <div className="flex basis-0 flex-grow-1 justify-end [&>*]:h-[28px]">
            {fixture.fw && <WidgetShowButton mobile={false}/>}
        </div> */}

    </div>);
};

export const MobileHeader: React.FC<HeaderProps> = ({
    handleBackToTournament,
    fixture
}) => {
    return (
        <div className="grid grid-cols-[40px_40px_1fr_40px] grid-rows-[minmax(31px,1fr)] border-b border-[#272d33] items-center">
            <IconButton onClick={handleBackToTournament}>
                <BackChevronIcon />
            </IconButton>
            <img
                className="w-[28px] h-[20px] mr-[10px]"
                src={fixture.ciu}
                alt={'flag'}
            />
            <div>
                {' '}
                {fixture.ctn}. {fixture.tn}
            </div>
            <div className='mobile:w-[32px] flex items-center justify-center h-[32px] bg-[#3FD96E] w-[max-content] text-[14px] py-[1px] px-[9px] rounded-[30px] ' style={{backgroundColor: hex2rgba(fixture.sc, 0.25)}}>
                <button className='flex justify-center align-center' onClick={() => copyLink()}>
                    <CopyIcon/>
                </button>
            </div>
        </div>
    );
};

const Info: React.FC<{
    status: FixtureStatus;
    competitors: Array<CompetitorElement>;
    startTime?: string;
    eventClock?: EventClockElement;
    periodScores?: Array<PeriodScoreElement>;
    homeScore?: number;
    awayScore?: number;
    competitorName: string;
    matchStatus?: string;
    sportId: number;
    fixtureWidgetId?: number;
}> = React.memo(
    ({
        status,
        competitors,
        startTime,
        eventClock,
        periodScores,
        homeScore,
        awayScore,
        competitorName,
        matchStatus,
        sportId,
        fixtureWidgetId
    }) => {
        const { t } = useTranslation();
        const {widget, showWidget} = useSelector((state: StoreType) => state.spirs);
        const dispatch = useDispatch();

        const periods: Array<PeriodScoreElement> | undefined = periodScores
            ? periodScores.filter(
                  period =>
                      period.periodNumber !== 100 &&
                      period.periodNumber !== 101 &&
                      period.periodNumber !== 102
              )
            : undefined;


        const scoreTable: IScores = scoreTableParser(
            t,
            periods,
            homeScore,
            awayScore
        );

        const date = startTime && formatDate(new Date(startTime));
        const c1 = competitors[0] ? competitors[0].n : '';
        const c2 = competitors[1] ? competitors[1].n : '';
        const prematch = React.useMemo(
            () =>
                date && (
                    <span className="text-center text-[14px] desktop:text-[20px]">
                        <span>{date.time}</span>{' '}
                        <span className="text-[rgb(246,212,75)]">
                            {date.date}
                        </span>
                    </span>
                ),
            [date]
        );
        const eventClockComponent = React.useMemo(
            () =>
                eventClock ? (
                    <div className="flex">
                        {matchStatus && <Time>{matchStatus}</Time>}
                        <Time>{eventClock.eventTime}"</Time>
                        <Yellow className='max-tablet:hidden'>Live</Yellow>
                    </div>
                ) : (
                    <div />
                ),
            [eventClock, matchStatus]
        );

        const score = React.useMemo(
            () =>
                homeScore ||
                (homeScore === 0 && awayScore) ||
                awayScore === 0 ? (
                    <Score>
                        <ScoreNumber>{homeScore}</ScoreNumber>
                        <ScoreNumber>{awayScore}</ScoreNumber>
                    </Score>
                ) : (
                    <div>-</div>
                ),
            [homeScore, awayScore]
        );

        /**
         * Таблица результатов матча
         */
        const table = React.useMemo(() => {
            if (!periods) {
                return periods;
            }


            return (
                <div
                    className={'grid w-max overflow-y-hidden whitespace-nowrap gap-x-[10px] [&>*:nth-child(1)]:mb-[10px]'}
                    style={{ gridTemplateColumns: `repeat(${scoreTable.columnsNumber}, 1fr)` }}
                >
                    {/*Начало шапки таблицы*/}
                    {scoreTable.header.cells.map((cell, i) => {
                        return (
                            <div
                                className='flex justify-center'
                                style={{ color: cell.color}}
                                key={i}
                            >
                                {i >= scoreTable.columnsNumber ? (
                                    cell.value
                                ) : (
                                    <PeriodName
                                        sportId={sportId}
                                        periodNumber={
                                            cell.value as number | undefined
                                        }
                                    />
                                )}
                            </div>
                        );
                    })}
                    {/*Конец шапки таблицы*/}

                    {/*Начало первой строки таблицы*/}
                    {scoreTable.scoresTable.homeScores.map((cell, i) => {
                        return (
                            <div
                                className='flex justify-center'
                                style={{ color: cell.color}}
                                key={i}
                            >
                                {cell.value}
                            </div>
                        );
                    })}
                    {/*Конец первой строки таблицы*/}

                    {/*Начало второй строки таблицы*/}
                    {scoreTable.scoresTable.awayScores.map((cell, i) => {
                        return (
                            <div
                                className='flex justify-center'
                                style={{ color: cell.color}}
                                key={i}
                            >
                                {cell.value}
                            </div>
                        );
                    })}
                    {/*Конец второй строки таблицы*/}
                </div>
            );
        }, [periods]);

        /**
         * Окно отображение хода матча для экранов больше планшета
         */
        const competitionDesktopLive = React.useMemo(() => {
            const scoreTable = scoreTableParser(t, periods, homeScore, awayScore);

            return (
                periods && <div className='flex flex-col gap-3'>
                    <div className='flex justify-between'>
                        <div>
                            {eventClockComponent}
                        </div>
                        <div className='flex'>
                            {scoreTable.header.cells.map((cell, i) => {
                                return (
                                    <div
                                        className='flex justify-center w-[55px]'
                                        style={{ color: cell.color}}
                                        key={i}
                                    >
                                        {i >= scoreTable.columnsNumber ? (
                                            cell.value
                                        ) : (
                                            <PeriodName
                                                sportId={sportId}
                                                periodNumber={
                                                    cell.value as number | undefined
                                                }
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className='flex justify-between'>
                        <div>
                            <TruncatedText
                                className="text-[16px] font-bold"
                                text={
                                    competitors.length === 2 ? c1 : competitorName
                                }
                            />
                        </div>
                        <div className='flex'>
                            {scoreTable.scoresTable.homeScores.map((cell, i) => {
                                return (
                                    <div
                                        className='flex justify-center w-[55px]'
                                        style={{ color: cell.color}}
                                        key={i}
                                    >
                                        {cell.value}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className='border-b border-[#ADABAB54] w-full my-[-6.5px]'/>
                    <div className='flex justify-between'>
                        <div>
                            {competitors.length === 2 && (
                                <TruncatedText
                                    className="text-[16px] font-bold"
                                    text={c2}
                                />
                            )}
                        </div>
                        <div className='flex'>
                            {scoreTable.scoresTable.awayScores.map((cell, i) => {
                                return (
                                    <div
                                        className='flex justify-center w-[55px]'
                                        style={{ color: cell.color}}
                                        key={i}
                                    >
                                        {cell.value}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }, [periods]);

        const tournamentStatusRender = () => {
            let statusText = "";

            switch (status) {
                case "FINISHED":
                case "ENDED":
                    statusText = t("fixture.finished");
                    break;
                case "CANCELLED":
                    statusText = t("fixture.cancelled");
                    break;
                case "POSTPONED":
                    statusText = t("fixture.postponed");
                    break;
                default: return null;
            }

            return <div className={'text-center text-[18px] font-medium'}>
                {statusText}
            </div>
        }

        const desktopRender = () => {
            return (
                <div className="flex flex-col gap-3 bg-[#1b1e23] py-[15px] px-[30px] rounded-[8px] min-h-[97px] max-w-2xl shadow-[0_2px_2px_0_rgba(0,0,0,0.5)] mt-3 mx-auto">
                    {tournamentStatusRender()}
                    {desktopCompetitionRender()}
                    {status === 'NOT_STARTED' ? prematch : competitionDesktopLive}
                </div>
            );
        }

        const desktopCompetitionRender = () => {
            return (
                <div className="">
                    {competitors.length === 2 ? (
                        <CompetitionName competitor1={c1} competitor2={c2}>
                            {score}
                        </CompetitionName>
                    ) : (
                        <div className="text-[24px] text-center">
                            {competitorName}
                        </div>
                    )}
                </div>
            )
        }

        const liveRender = () => {
            return (
                <div className="flex justify-between p-[15px] tablet:hidden">
                    <div className="flex flex-col">
                        <div className="grid grid-cols-3 mb-[10px]">
                            {eventClockComponent}
                        </div>
                        <TruncatedText
                            className="text-[16px] font-bold"
                            text={
                                competitors.length === 2 ? c1 : competitorName
                            }
                        />
                        {competitors.length === 2 && (
                            <TruncatedText
                                className="text-[16px] font-bold"
                                text={c2}
                            />
                        )}
                    </div>

                    <div className="flex w-full justify-end">
                        {table}
                    </div>
                </div>
            );
        };

        const prematchMobileRender = () => {
            return (
                <div className="flex flex-col justify-center mt-4 gap-4 tablet:hidden">
                    <div className="text-center font-bold">
                        {competitors.length === 2
                            ? `${c1} - ${c2}`
                            : competitorName}
                    </div>
                    <div className='flex justify-center'>
                        {prematch}
                    </div>
                </div>
            );
        };

        /**
         * Компонент отображения кнопок управления виджетами, при их наличии
         */
        const footer = () => {
            if (!widget.widgetId) {
                return null;
            }
            return (
                <div className="flex justify-center gap-4">
                    <MatchInfoIcon
                        className={clsx(
                            'w-[30px] h-[30px]',
                            showWidget ? 'fill-[#85A7A2]' : 'fill-[#FFFFFF]'
                        )}
                        onClick={() =>
                            showWidget && dispatch(SHOW_WIDGET(!showWidget))
                        }
                    />
                    <WidgetShowButton mobile={true} />
                </div>
            )
        }

        /**
         * компонент отображения виджета, при его наличии
         */
        const widgetRender = () => {
            return (
                <div className='flex flex-col my-4 gap-4'>
                    {desktopCompetitionRender()}
                    <WidgetsPane />
                </div>
            )
        };

        return (
            <div className="flex flex-col">
                <div className='max-tablet:hidden'>
                    {desktopRender()}
                </div>
                <span className='tablet:hidden'>
                    {tournamentStatusRender()}
                </span>
                {showWidget
                    ? widgetRender()
                    : status === 'NOT_STARTED'
                    ? prematchMobileRender()
                        : liveRender()
                }
                <Mobile>{footer()}</Mobile>
            </div>
        );
    }
);

const CompetitionName: React.FC<React.PropsWithChildren<{
    competitor1: string;
    competitor2: string;
}>> = React.memo(({ competitor1, competitor2, children }) => {
    return (
        <div className="flex items-center justify-center text-[20px] gap-[15px]">
            <div className="flex text-right">{competitor1}</div>
            <div>{children}</div>
            <div className="flex text-left">{competitor2}</div>
        </div>
    );
});

interface GroupMarketsProps {
    period: string;
    groupedMarkets: GroupedMarketElements[];
    fixture: FixtureElement;
    debug: boolean;
    open: boolean;
    hasOverlay: boolean;
}

const GroupDesktopMarkets: React.FC<GroupMarketsProps> = props => {
    const { period, fixture, open } = props;
    const [isOpen, setOpen] = useState<boolean>(open);
    const handleChange = () => setOpen(p => !p);
    return (
        <>
            <div
                style={{ color: fixture.sc }}
                className={`flex items-center justify-center bg-[#171A1E] text-[20px] py-[10px] pr-[4px] pl-[15px] rounded-[8px] mb-[8px] cursor-pointer`}
                onClick={handleChange}
            >
                <span className="text-[#D09E00]">{period}</span>

                <div className="ml-auto w-[29px]">
                    <MoreLessToggleIcon isOpen={isOpen} doubleArrow />
                </div>
            </div>
            <Visible value={isOpen}>
                <GroupMarkets {...props} />
            </Visible>
        </>
    );
};

interface GroupMobileMarketsProps extends GroupMarketsProps {
    currentGroup: PeriodElement['g'] | null
}

const GroupMobileMarkets: React.FC<GroupMobileMarketsProps> = props => {
    const groupedMarkets = props.fixture.gm.filter(gr =>
        gr.m.every(market => market.pg === props.currentGroup)
    );
    return <GroupMarkets {...props} groupedMarkets={groupedMarkets} />;
};

const GroupMarkets: React.FC<GroupMarketsProps> = ({
    groupedMarkets,
    fixture,
    debug,
    hasOverlay
}) => {
    return (
        <>
            {groupedMarkets.map((groupedMarkets, i) => (
                <MarketVerbose
                    groupedMarkets={groupedMarkets}
                    key={i}
                    fixture={fixture}
                    debug={debug}
                    hasOverlay={hasOverlay}
                />
            ))}
        </>
    );
};

interface MobilePeriodsProps {
    periods: PeriodElement[];
    callback: Dispatch<SetStateAction<PeriodElement['g'] | null>>;
    current: string | null;
}

const MobilePeriods: React.FC<MobilePeriodsProps> = ({
    periods,
    callback,
    current
}) => {
    const { t } = useTranslation();
    const [viewportWidth, setViewportWidth] = useState(0);

    React.useEffect(() => {
        const w = Math.min(
            window.visualViewport?.width || Infinity,
            document.documentElement.clientWidth || Infinity,
            window.innerWidth || Infinity
        );
        const updateWidth = () => setViewportWidth(w - 40); //40 = padding-x
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const handleClick = (g: PeriodElement['g']) => () => callback(g);

    return (
        <div className="w-full bg-[#171a1e] mb-[10px]">
            <div className="overflow-y-hidden whitespace-nowrap" style={{ width: viewportWidth }}>
                {periods.map((period, i) => (
                    <div
                        key={i}
                        className="inline-block p-[10px] pb-0 bg-[#171a1e]"
                        onClick={handleClick(period.g)}
                    >
                        <div
                            className={clsx(
                                'pb-[5px]',
                                current === period.g
                                    ? 'border-b-[2px] tablet:border-[2px] border-[#d32f2f]'
                                    : 'border-b-[2px] tablet:border-[2px] border-[#171a1e]'
                            )}
                        >
                            {t(period.n)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const Bordered = styled.div`
    border-bottom: 1px solid #adabab54;
`;

const CompetitorName = styled.div`
    font-size: 16px;
`;

const Grey = styled.div`
    color: #adabab;
    font-size: 16px;
`;

const Time = styled.div`
    color: #adabab;
    margin-right: 15px;
    font-size: 16px;
`;

const Yellow = styled.div`
    color: #ffdb4d;
    font-size: 16px;
`;

const Score = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 48px);
    column-gap: 6px;
`;

const ScoreNumber = styled.div`
    color: #ffdb4d;
    font-size: 24px;
    font-weight: bold;
    letter-spacing: 1.33px;
    text-align: center;
    height: 36px;
    width: 48px;
    border-radius: 8px;
    background-color: #272d33;
`;

const ColoredNumber = styled.div`
    color: #ffdb4d;
    text-align: center;
    font-size: 16px;
`;

const YellowCard = styled.div`
    width: 8px;
    height: 13px;
    border-radius: 2px;
    background-color: #ebe63d;
`;

const RedCard = styled.div`
    width: 8px;
    height: 13px;
    border-radius: 2px;
    background-color: #e73838;
`;
